import { CircleCheck, CircleX, Info, LucideIcon, TriangleAlert, X } from 'lucide-react';
import { CSSProperties, PropsWithChildren } from 'react';

export type ToastProps = PropsWithChildren<{
  title?: string;
  message?: React.ReactNode;
  type: 'success' | 'error' | 'info' | 'warning';
  onClose?: () => void;
}>;

type ToastTone = {
  /** Semantic colour token for the icon, the title and the tint of the surface. */
  tone: string;
  /** Its border partner. Positive has none of its own; accent shares the hue. */
  border: string;
  /** Shape carries the type as well as the colour does (WCAG 1.4.1). */
  Icon: LucideIcon;
};

/**
 * The type of a toast selects a pair of theme tokens, and the `.toast` rules in index.css
 * derive the surface, the border and the text colours from them. It used to select a Tailwind
 * colour family and interpolate it into class names (`bg-${baseColor}-50`); Tailwind's JIT only
 * compiles complete literal class strings, so most of those utilities were never generated
 * and three of the four types shipped with no border colour and two with no icon colour
 * (DLI-1848). Keep the mapping token-valued: a class name assembled at runtime cannot be
 * seen by the build-time scanner that has to emit it.
 */
const TONE_BY_TYPE: Record<ToastProps['type'], ToastTone> = {
  success: { tone: '--positive', border: '--accent-border', Icon: CircleCheck },
  error: { tone: '--danger', border: '--danger-border', Icon: CircleX },
  warning: { tone: '--warning', border: '--warning-border', Icon: TriangleAlert },
  info: { tone: '--info', border: '--info-border', Icon: Info },
};

export const Toast: React.FC<ToastProps> = ({ title, message, children, type, onClose }) => {
  const { tone, border, Icon } = TONE_BY_TYPE[type];
  const toneStyle = {
    '--toast-tone': `var(${tone})`,
    '--toast-border': `var(${border})`,
  } as CSSProperties;

  return (
    <div className="toast flex items-start p-4 rounded-lg" style={toneStyle}>
      <div className="flex-shrink-0">
        <Icon className="toast-icon w-5 h-5" aria-hidden="true" />
      </div>
      <div className="ml-3">
        {title && <h3 className="toast-title text-sm font-medium">{title}</h3>}
        {message && <p className="toast-message mt-1 text-sm">{message}</p>}
        {children}
      </div>
      {onClose && (
        <button
          type="button"
          className="toast-close ml-auto"
          aria-label="Dismiss"
          onClick={() => onClose()}
        >
          <X className="w-4 h-4" aria-hidden="true" />
        </button>
      )}
    </div>
  );
};
